// crewbit-admin/src/pages/Money/Settings/Landing.tsx
//
// /money/settings — landing screen. PageHeader + 4-card menu +
// ExportMoneyDataCard. Outer <RequiresActiveBusinessGate> in App.tsx
// short-circuits without an active business.
//
// Tier 2-UI Phase A §WorldClass.4 — owner-only BootstrapWizardCard mounted
// above the 4-card menu when role === 'owner'. Card opens a modal-mounted
// BootstrapWizard for re-entry per Decision §A.D1 (single component, two
// contexts).

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import SettingsLandingMenu from '../../../components/money/settings/SettingsLandingMenu';
import ExportMoneyDataCard from '../../../components/money/settings/ExportMoneyDataCard';
import BootstrapWizardCard from '../../../components/money/bootstrap/BootstrapWizardCard';
import BootstrapWizard from '../../../components/money/bootstrap/BootstrapWizard';
import type { ProvinceCode } from '../../../components/money/bootstrap/ProvincePicker';
import { Toast } from '../../../components/common/Toast';
import { useActiveBusinessStore } from '../../../hooks/useActiveBusiness';
import { useAuthStore } from '../../../stores/authStore';
import { supabase } from '../../../lib/supabase';
import { admin, palette, radii, spacing } from '../../../styles/tokens';
import type { OnboardingAnswers } from '../../../types/business';

const pageStyle: React.CSSProperties = {
  padding: spacing.xl,
  background: admin.bg,
  minHeight: '100vh',
};

interface BootstrapBizRow {
  province: string | null;
  activity_class: string | null;
  onboarding_answers: OnboardingAnswers | null;
}

export function MoneySettingsLanding() {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const role = useAuthStore((s) => s.user?.role);
  const isOwner = role === 'owner';

  const [bizRow, setBizRow] = useState<BootstrapBizRow | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' } | null>(null);

  useEffect(() => {
    document.title = 'CrewBit · Money settings';
  }, []);

  useEffect(() => {
    if (!businessId || !isOwner) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('businesses')
        .select('province, activity_class, onboarding_answers')
        .eq('id', businessId)
        .maybeSingle();
      if (cancelled) return;
      setBizRow((data ?? null) as BootstrapBizRow | null);
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, isOwner, wizardOpen]);

  const subIndustryId =
    (bizRow?.onboarding_answers as { subIndustryId?: string | null } | null | undefined)
      ?.subIndustryId ?? null;
  const initialProvince = (bizRow?.province ?? null) as ProvinceCode | null;
  const hasRun = Boolean(bizRow?.activity_class);

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Money settings"
        description="Defaults, tax setup, accountant access, notifications."
        back={{ to: '/money', label: 'Money' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        {isOwner && businessId ? (
          <BootstrapWizardCard hasRun={hasRun} onClick={() => setWizardOpen(true)} />
        ) : null}
        <SettingsLandingMenu businessId={businessId} />
        <ExportMoneyDataCard businessId={businessId} />
      </div>

      {wizardOpen && businessId ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setWizardOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingBlock: spacing.xl,
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: admin.bg,
              borderRadius: admin.cardRadius,
              padding: spacing.xl,
              width: '100%',
              maxWidth: 600,
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={() => setWizardOpen(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: spacing.md,
                right: spacing.md,
                width: 32,
                height: 32,
                borderRadius: radii.md,
                border: 'none',
                background: palette.g100,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} color={palette.g600} />
            </button>
            <BootstrapWizard
              businessId={businessId}
              initialProvince={initialProvince}
              subIndustryId={subIndustryId}
              onSuccess={() => {
                setWizardOpen(false);
                setToast({
                  message: 'Defaults updated. Your existing customizations are preserved.',
                  type: 'success',
                });
              }}
              onSkip={() => setWizardOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <Toast
        message={toast?.message ?? ''}
        type="success"
        isVisible={toast !== null}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}
